import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // 👈 import navigation hook
import "./ManageQuestionnaireResponses.css";
import publicAxios from "../../utils/axios/publicAxios";
import moment from "moment";

const ManageQuestionnaireResponses = () => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate(); // 👈 initialize router navigation

  // Fetch all questionnaire responses
  const fetchResponses = async (pageNumber = 1, searchTerm = "") => {
    setLoading(true);
    try {
      const endpoint = searchTerm
        ? `/public/manage-search-questionnaire-responses?query=${encodeURIComponent(
            searchTerm
          )}&page=${pageNumber}`
        : `/public/manage-questionnaire-responses?page=${pageNumber}`;

      const res = await publicAxios.get(endpoint);

      if (res.data.success) {
        setResponses(res.data.responses);
        setTotalPages(res.data.totalPages);
        setPage(res.data.currentPage);
        setMessage("");
      } else {
        setMessage("⚠️ Unable to load questionnaire responses.");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setMessage("❌ Error fetching questionnaire responses.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch responses on mount & when page changes
  useEffect(() => {
    fetchResponses(page, search);
  }, [page]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchResponses(1, search);
  };

  // Navigate to the response details page
  const handleRowClick = (slug) => {
    if (slug) {
      navigate(`/manage-questionnaire-response/${slug}`);
    }
  };

  return (
    <div className="manage-responses-container">
      <div className="manage-responses-header">
        <h1>All Questionnaire Responses</h1>
        <p className="subtitle">
          View all submitted questionnaire responses. Use the search to find
          responses by questionnaire title or slug.
        </p>
      </div>

      {/* Search bar */}
      <form className="search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search responses by title or slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit">Search</button>
      </form>

      {loading ? (
        <p className="loading-text">Loading responses...</p>
      ) : responses.length === 0 ? (
        <p className="empty-text">No responses found.</p>
      ) : (
        <div className="responses-table-wrapper">
          <table className="responses-table">
            <thead>
              <tr>
                <th>Questionnaire</th>
                <th>Creator</th>
                <th>Respondent</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <tr
                  key={r._id}
                  className="clickable-row" // 👈 style for hover and cursor
                  onClick={() => handleRowClick(r.questionnaireSlug)} // 👈 click handler
                >
                  <td>
                    <div className="cell-title">
                      <strong>{r.questionnaire?.title || "Untitled"}</strong>
                      <span className="slug">
                        {r.questionnaireSlug || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td>
                    {r.createdBy
                      ? `${r.createdBy.firstname} ${r.createdBy.lastname}`
                      : "—"}
                  </td>
                  <td>
                    {r.respondent
                      ? `${r.respondent.firstname} ${r.respondent.lastname}`
                      : "Anonymous"}
                  </td>
                  <td>{moment(r.submittedAt).format("MMM DD, YYYY HH:mm")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            ◀ Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ▶
          </button>
        </div>
      )}

      {message && <p className="status-message">{message}</p>}
    </div>
  );
};

export default ManageQuestionnaireResponses;